import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KeyRound, ArrowRight } from 'lucide-react';
import { useCarrierCredentialHealth } from '@/hooks/useCarrierCredentialHealth';
import type { CarrierProvider } from '@/services/api/carrier-credential-health';

const CARRIER_NAMES: Record<CarrierProvider, string> = {
  dhl: 'DHL',
  dpd: 'DPD',
  gls: 'GLS',
  sendcloud: 'Sendcloud',
};

const formatDate = (iso: string, locale: string): string =>
  new Date(iso).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });

export const CarrierCredentialBanner = () => {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useCarrierCredentialHealth();

  if (isLoading) return null;
  const invalid = (data?.carriers ?? []).filter((c) => c.invalid_since);
  if (invalid.length === 0) return null;

  return (
    <div
      role="alert"
      className="mx-4 mt-4 animate-fade-in rounded-card border border-error/30 bg-error/5 p-4"
    >
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-start gap-3">
          <KeyRound className="h-5 w-5 shrink-0 text-error" />
          <div className="space-y-1">
            {invalid.map((c) => (
              <p key={c.provider} className="text-sm font-medium text-error">
                {t('carrierCredentials.bannerInvalid', {
                  carrier: CARRIER_NAMES[c.provider],
                  date: formatDate(c.invalid_since as string, i18n.language),
                })}
              </p>
            ))}
          </div>
        </div>
        <Link
          to="/integrations"
          className="bg-gradient-orange hover-glow btn btn-sm min-h-touch gap-1 rounded-btn text-black"
        >
          {t('carrierCredentials.fixNow')}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};
